import { type LedgerEligibility } from '@heidi/capture/ports';
import { FEATURE_FLAG_DOMAIN } from '@heidi/runtime/feature-flags';
import { OpenFeature, ProviderEvents, ProviderStatus } from '@openfeature/react-sdk';

const RECORDING_STORE_V2_FLAG_KEY = 'release-scribe-audio-ledger';

/**
 * Stamps the resolved cohort onto Datadog RUM global context. (Logs are tagged
 * inside `@heidi/capture`, at the pin.)
 *
 * Dynamic and un-awaited: this module is reachable from `@/composition/hosts/capture/capture-wiring`,
 * which `_app.tsx` evaluates before `@/lib/utils`, so a static import would hoist
 * the Datadog SDKs into that window. A failed import leaves the page unstamped
 * rather than delaying the pin.
 */
const stampPipelineModeCohort = (enabled: boolean): void => {
  void import('@/utils/tracing')
    .then(({ setDatadogPipelineModeContext }) =>
      setDatadogPipelineModeContext(enabled ? 'ledger' : 'legacy')
    )
    .catch(() => {});
};

/**
 * Resolves the v2 persistence flag after a bounded provider-ready wait.
 * The reason labels the legacy-pin cohort in `LEDGER_MODE_PINNED` telemetry:
 * a provider timeout must be distinguishable from a deliberate flag-off.
 */
export const resolveRecordingStoreV2Enabled = async (
  timeoutMs = 3000
): Promise<LedgerEligibility> => {
  const client = OpenFeature.getClient(FEATURE_FLAG_DOMAIN);
  let settledBy: 'ready' | 'error' | 'timeout' = 'ready';
  if (client.providerStatus !== ProviderStatus.READY) {
    settledBy = await new Promise<'ready' | 'error' | 'timeout'>((resolve) => {
      const settle = (outcome: 'ready' | 'error' | 'timeout') => () => {
        clearTimeout(timer);
        client.removeHandler(ProviderEvents.Ready, onReady);
        client.removeHandler(ProviderEvents.Error, onError);
        resolve(outcome);
      };
      const onReady = settle('ready');
      const onError = settle('error');
      const timer = setTimeout(settle('timeout'), timeoutMs);
      client.addHandler(ProviderEvents.Ready, onReady);
      client.addHandler(ProviderEvents.Error, onError);
    });
  }
  if (client.providerStatus !== ProviderStatus.READY) {
    stampPipelineModeCohort(false);
    return {
      enabled: false,
      reason: settledBy === 'error' ? 'provider-error' : 'provider-timeout',
    };
  }
  const enabled = client.getBooleanValue(RECORDING_STORE_V2_FLAG_KEY, false);
  stampPipelineModeCohort(enabled);
  return { enabled, reason: 'flag' };
};
